
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    // バリデーション
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('必須項目を入力してください。');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('メッセージは500文字以内で入力してください。');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d3vhsprjbvf464v00u70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        setSubmitStatus('お問い合わせを受け付けました。ありがとうございます。');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      setSubmitStatus('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
            お問い合わせ
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            税務に関するご相談やサービスについてのお問い合わせは、お気軽にご連絡ください
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12">
          {/* お問い合わせフォーム */}
          <div className="bg-white rounded-lg shadow-lg p-5 sm:p-6 md:p-8">
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-5 sm:mb-6">
              お問い合わせフォーム
            </h3>
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    会社名・団体名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="service" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  ご相談内容
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">選択してください</option>
                  <option value="税務申告">税務申告</option>
                  <option value="経理代行">経理代行</option>
                  <option value="給与計算">給与計算</option>
                  <option value="会社設立支援">会社設立支援</option>
                  <option value="経営コンサルティング">経営コンサルティング</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  メッセージ <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="ご相談内容やご質問をお聞かせください（500文字以内）"
                  required
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500文字
                </div>
              </div>

              {submitStatus && (
                <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg text-sm ${
                  submitStatus.includes('受け付けました') 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {submitStatus}
                </div>
              )}

              <Button 
                type="submit" 
                size="lg" 
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? '送信中...' : 'お問い合わせを送信'}
              </Button>
            </form>
          </div>

          {/* 連絡先情報 */}
          <div>
            <div className="bg-white rounded-lg shadow-lg p-5 sm:p-6 md:p-8 mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-5 sm:mb-6">
                直接のお問い合わせ
              </h3>
              <div className="space-y-5 sm:space-y-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-lg flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm sm:text-base font-semibold text-gray-900">電話でのお問い合わせ</div>
                    <div className="text-xl sm:text-2xl font-bold text-blue-600 my-1 sm:my-2">03-1234-5678</div>
                    <div className="text-xs sm:text-sm text-gray-600 break-words">
                      受付時間：平日 9:00-18:00<br />
                      初回相談無料
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-lg flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm sm:text-base font-semibold text-gray-900">メールでのお問い合わせ</div>
                    <div className="text-sm sm:text-base text-blue-600 my-1 sm:my-2 break-all">info@tax-office.co.jp</div>
                    <div className="text-xs sm:text-sm text-gray-600 break-words">
                      24時間受付<br />
                      2営業日以内にご返信いたします
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm sm:text-base font-semibold text-gray-900">所在地</div>
                    <div className="text-xs sm:text-sm text-gray-600 my-1 sm:my-2 break-words">
                      〒100-0001<br />
                      東京都千代田区千代田1-1-1<br />
                      千代田ビル5階
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 break-words">
                      JR東京駅より徒歩5分<br />
                      地下鉄大手町駅より徒歩3分
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 営業時間 */}
            <div className="bg-blue-50 rounded-lg p-5 sm:p-6">
              <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">営業時間</h4>
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span>月曜日 - 金曜日</span>
                  <span className="font-medium">9:00 - 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span>土曜日</span>
                  <span className="font-medium">9:00 - 15:00</span>
                </div>
                <div className="flex justify-between">
                  <span>日曜日・祝日</span>
                  <span className="text-red-600">休業</span>
                </div>
              </div>
              <div className="mt-3 sm:mt-4 text-xs text-gray-600">
                ※緊急時は24時間対応いたします
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
